import { zodResolver } from "@hookform/resolvers/zod";
import { Checkbox } from "@mui/material";
import { Box } from "@mui/system";
import { FunctionComponent, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Link, NavLink, useNavigate } from "react-router-dom";

import FormTextField from "@/pages/Private/pages/Account/components/FormTextField";

import { ROUTE_CONFIG } from "@/routes/config";

import FormContainer from "../../components/FormContainer";
import { useRegisterMutation } from "../../redux/questionnaire/questionnaire.api";
import { QuestionnaireImportance } from "../Questionnaire/enum/QuestionnaireImportance.enum";

import { RegisterSchema, RegisterType } from "./schema/register";

const { CHECK_EMAIL, LOGIN, PRIVACY } = ROUTE_CONFIG;

export const Register: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`register.${key}`);
	const navigate = useNavigate();

	const [registerUser, { isLoading, error, isSuccess }] = useRegisterMutation();

	const initialValues: RegisterType = {
		firstName: "",
		lastName: "",
		email: "",
		companyName: "",
		privacyStatement: false,
	};

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm<RegisterType>({
		defaultValues: initialValues,
		resolver: zodResolver(RegisterSchema),
	});

	const onSubmit = async (values: RegisterType) => {
		try {
			await registerUser({
				...values,
				importance: QuestionnaireImportance.IMPORTANT,
			}).unwrap();
		} catch (err) {
			console.error(err);
		}
	};

	useEffect(() => {
		if (isSuccess) {
			navigate(CHECK_EMAIL, { state: { email: watch("email") } });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccess, navigate]);

	return (
		<div className="w-full h-full flex items-center justify-center">
			<FormContainer
				description={ts("description")}
				formContainerClassName="min-h-fit my-6"
				formError={error}
				formFooter={
					<div className="flex flex-col gap-2">
						<Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
							<Checkbox
								sx={
									errors?.privacyStatement?.message
										? { color: "red", m: 0, p: 0 }
										: { color: "#E6E6EC", m: 0, p: 0 }
								}
								{...register("privacyStatement")}
							/>

							<span className="text-xs sm:text-sm text-primary-gray-lighter inline float-left">
								{t("basics.privacyStatementObject.noLink")}
								<Link
									className="text-progress-bar mr-1 font-normal inline"
									target="_blank"
									to={PRIVACY}
								>
									{t("basics.privacyStatementObject.link1")}
								</Link>
								{t("basics.privacyStatementObject.noLink2")}
							</span>
						</Box>
						<NavLink className="text-[14px] text-primary-blue-hover text-nowrap" to={LOGIN}>
							{ts("haveAccount")}
						</NavLink>
					</div>
				}
				handleSubmit={handleSubmit(onSubmit)}
				isLoading={isLoading}
				submitButtonText={ts("registerCta")}
				title={ts("title")}
			>
				<FormTextField
					autoComplete="firstName"
					errorMessage={errors?.firstName?.message}
					formRegister={register("firstName")}
					label={t("basics.firstName")}
					placeHolder={t("basics.enterFirstName")}
				/>
				<FormTextField
					autoComplete="lastName"
					errorMessage={errors?.lastName?.message}
					formRegister={register("lastName")}
					label={t("basics.lastName")}
					placeHolder={t("basics.enterLastName")}
				/>
				<FormTextField
					autoComplete="email"
					errorMessage={errors?.email?.message}
					formRegister={register("email")}
					label={t("basics.email")}
					type="email"
				/>
				<FormTextField
					autoComplete="companyName"
					errorMessage={errors?.companyName?.message}
					formRegister={register("companyName")}
					label={t("basics.companyName")}
					placeHolder={t("basics.enterCompanyName")}
				/>
			</FormContainer>
		</div>
	);
};
